import { createAction, handleActions, type Action } from 'redux-actions';
import type { Dispatch } from 'react';
import type { OneShotEditQuoteState, OneShotEditQuoteTab } from '../types';
import type { FirmographicDataDto } from '../../../../../../../../types/firmo/firmographic-data.dto';
import type { FieldValue } from '../../../../../../../../form-engine/types';
import { CoverageState, RatingOptions } from '../../../../types';
import {
  RaterXPreset,
  ValueConfig,
} from '../../../../api/types/rater-x-preset';

const INITIAL_STATE: OneShotEditQuoteState = {
  isUpdatingFromCalculate: false,
  hasCalculateWarnings: false,
} as OneShotEditQuoteState;

const SET_COVERAGE_STATE = 'SET_COVERAGE_STATE';
const SET_FIRMO_STATE = 'SET_FIRMO_STATE';
const SET_APPLICATION_DETAILS = 'SET_APPLICATION_DETAILS';
const SET_ACTIVE_TAB = 'SET_ACTIVE_TAB';
const SET_DIRTY = 'SET_DIRTY';
const SET_CALCULATING = 'SET_CALCULATING';
const SET_SUBMITTING = 'SET_SUBMITTING';
const SET_VALIDATION_ERRORS = 'SET_VALIDATION_ERRORS';
const SET_CALCULATE_VALID = 'SET_CALCULATE_VALID';
const SET_RATER_X_DATA = 'SET_RATER_X_DATA';
const SET_UPDATING_FROM_CALCULATE = 'SET_UPDATING_FROM_CALCULATE';
const SET_DECLINATION_INFO = 'SET_DECLINATION_INFO';
const SET_CALCULATE_WARNINGS = 'SET_CALCULATE_WARNINGS';
const RESET_STATE = 'RESET_STATE';

export const createOneShotActionHandlers = (
  dispatch: Dispatch<Action<OneShotEditQuoteState>>
) => {
  return {
    setCoverageState: (payload: CoverageStatePayload) =>
      dispatch(createAction(SET_COVERAGE_STATE)(payload)),

    setFirmoState: (payload: FirmoStatePayload) =>
      dispatch(createAction(SET_FIRMO_STATE)(payload)),

    setApplicationDetails: (payload: ApplicationDetailsPayload) =>
      dispatch(createAction(SET_APPLICATION_DETAILS)(payload)),

    setActiveTab: (payload: OneShotEditQuoteTab) =>
      dispatch(createAction(SET_ACTIVE_TAB)(payload)),

    setDirty: (payload: boolean) => dispatch(createAction(SET_DIRTY)(payload)),

    setCalculating: (payload: boolean) =>
      dispatch(createAction(SET_CALCULATING)(payload)),

    setSubmitting: (payload: boolean) =>
      dispatch(createAction(SET_SUBMITTING)(payload)),

    setValidationErrors: (payload: Record<string, string[]>) =>
      dispatch(createAction(SET_VALIDATION_ERRORS)(payload)),

    setCalculateValid: (payload: boolean) =>
      dispatch(createAction(SET_CALCULATE_VALID)(payload)),

    setRaterXData: (payload: any) =>
      dispatch(createAction(SET_RATER_X_DATA)(payload)),

    setUpdatingFromCalculate: (payload: boolean) =>
      dispatch(createAction(SET_UPDATING_FROM_CALCULATE)(payload)),

    setDeclinationInfo: (payload: DeclinationInfoPayload) =>
      dispatch(createAction(SET_DECLINATION_INFO)(payload)),

    setCalculateWarnings: (payload: boolean) =>
      dispatch(createAction(SET_CALCULATE_WARNINGS)(payload)),

    resetState: (payload: OneShotEditQuoteState) =>
      dispatch(createAction(RESET_STATE)(payload)),
  };
};

export const oneShotEditQuoteReducer = handleActions<
  OneShotEditQuoteState,
  any
>(
  {
    [SET_COVERAGE_STATE]: (state, action: Action<CoverageStatePayload>) => {
      const payload = action.payload || {};
      // Prevent false "dirty" flag during initial load when effectiveDate arrives before coverageState
      const isInitialSync =
        !state.effectiveDate &&
        Object.keys(state.coverageState || {}).length === 0;
      return {
        ...state,
        ...payload,
        isDirty: isInitialSync ? state.isDirty : true,
      };
    },

    [SET_FIRMO_STATE]: (state, action: Action<FirmoStatePayload>) => {
      const firmoData = action.payload;
      const isInitialSync =
        !state.firmographics || Object.keys(state.firmographics).length === 0;
      return {
        ...state,
        firmographics: firmoData as FirmographicDataDto,
        isDirty: isInitialSync ? state.isDirty : true,
      };
    },

    [SET_APPLICATION_DETAILS]: (
      state,
      action: Action<ApplicationDetailsPayload>
    ) => {
      const payload = action.payload;
      const isInitialSync =
        !state.applicationDetails ||
        Object.keys(state.applicationDetails).length === 0;

      const newApplicationDetails = {
        ...(state.applicationDetails || {}),
        ...(payload || {}),
      };

      // TODO: Replace JSON.stringify comparison with proper deep equality check
      // JSON.stringify has issues: property order matters, undefined values are dropped,
      // and it doesn't handle nested FieldValue structures correctly.
      // Consider using lodash.isEqual or a custom FieldValue comparison function.
      const hasChanges =
        JSON.stringify(state.applicationDetails || {}) !==
        JSON.stringify(newApplicationDetails);

      if (!hasChanges && !isInitialSync) {
        return state;
      }

      return {
        ...state,
        applicationDetails: newApplicationDetails,
        isDirty: isInitialSync ? state.isDirty : true,
      };
    },

    [SET_ACTIVE_TAB]: (state, action: Action<OneShotEditQuoteTab>) => ({
      ...state,
      activeTab: action.payload,
    }),

    [SET_DIRTY]: (state, action: Action<boolean>) => ({
      ...state,
      isDirty: action.payload,
    }),

    [SET_CALCULATING]: (state, action: Action<boolean>) => ({
      ...state,
      isCalculating: action.payload,
    }),

    [SET_SUBMITTING]: (state, action: Action<boolean>) => ({
      ...state,
      isSubmitting: action.payload,
    }),

    [SET_VALIDATION_ERRORS]: (
      state,
      action: Action<Record<string, string[]>>
    ) => ({
      ...state,
      validationErrors: action.payload || {},
    }),

    [SET_CALCULATE_VALID]: (state, action: Action<boolean>) => ({
      ...state,
      isCalculateValid: action.payload,
    }),

    [SET_RATER_X_DATA]: (state, action: Action<any>) => ({
      ...state,
      raterXData: action.payload,
    }),

    [SET_UPDATING_FROM_CALCULATE]: (state, action: Action<boolean>) => ({
      ...state,
      isUpdatingFromCalculate: action.payload,
    }),

    [SET_DECLINATION_INFO]: (
      state,
      action: Action<DeclinationInfoPayload>
    ) => ({
      ...state,
      declinationReasons: action.payload?.declinationReasons ?? [],
    }),

    [SET_CALCULATE_WARNINGS]: (state, action: Action<boolean>) => ({
      ...state,
      hasCalculateWarnings: action.payload,
    }),

    [RESET_STATE]: (state, action: Action<OneShotEditQuoteState>) =>
      action.payload,
  },
  INITIAL_STATE
);

type CoverageStatePayload = Partial<{
  coverageState: CoverageState;
  ratingOptions: RatingOptions;
  global: RaterXPreset['global'];
  componentGlobals: OneShotEditQuoteState['componentGlobals'];
  fieldValues: Record<string, unknown>[];
  effectiveDate: string;
  endDate: string;
  policyValue: ValueConfig;
  components: Record<string, unknown>;
  commissionRate: number;
  isRenewal: boolean;
  carrierPrefix: string;
  treatyId: string;
}>;

type FirmoStatePayload = FirmographicDataDto;

type ApplicationDetailsPayload = Record<string, FieldValue>;

type DeclinationInfoPayload = {
  declinationReasons?: string[];
};

export type OneShotEditQuoteHandlers = ReturnType<
  typeof createOneShotActionHandlers
>;
